import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Search, LogOut, BookOpen, Target, ExternalLink, ListChecks } from "lucide-react";
import BrandMark from "@/components/site/Wordmark";
import KnowledgeLibrary from "@/components/app/KnowledgeLibrary";
import MandateEditor from "@/components/app/MandateEditor";
import OnboardingPanel from "@/components/app/OnboardingPanel";

export default function AppShell() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("onboarding");

  useEffect(() => {
    document.title = "Workspace | ImpactVest";
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-iv-bg">
        <span className="font-mono text-[12px] uppercase tracking-widest text-iv-fg-3">Loading workspace…</span>
      </div>
    );
  }
  if (!user) return null;

  const roleLabel = {
    platform_admin: "Platform Admin",
    pilot_admin: "Organization Admin",
    pilot_user: "Pilot User",
    committee_viewer: "Committee Viewer",
    investment_professional: "Investment Professional",
  }[user.role] || user.role;

  return (
    <div className="min-h-screen bg-iv-bg">
      {/* Top bar */}
      <header className="border-b border-iv-line bg-iv-surface sticky top-0 z-40">
        <div className="iv-container flex items-center justify-between h-[64px]">
          <div className="flex items-center gap-4">
            <BrandMark size={36} to="/" />
            <span className="hidden md:inline font-mono-tag text-iv-fg-3">Workspace</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <div className="text-[13px] text-iv-fg leading-tight">{user.full_name}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-iv-fg-3">
                {user.organization} · {roleLabel}
              </div>
            </div>
            <button
              data-testid="app-logout"
              onClick={() => { logout(); navigate("/"); }}
              className="iv-btn iv-btn--ghost"
              aria-label="Sign out"
            >
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="iv-container py-8 md:py-12">
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-8 border-b border-iv-line overflow-x-auto">
          <TabBtn active={tab === "onboarding"} onClick={() => setTab("onboarding")} icon={<ListChecks size={15} />} label="Onboarding" testid="tab-onboarding" />
          <TabBtn active={tab === "mandate"} onClick={() => setTab("mandate")} icon={<Target size={15} />} label="Capital Mandate" testid="tab-mandate" />
          <TabBtn active={tab === "knowledge"} onClick={() => setTab("knowledge")} icon={<BookOpen size={15} />} label="ImpactVest Knowledge" testid="tab-knowledge" />
        </div>

        {tab === "onboarding" && <OnboardingPanel onOpenTab={setTab} />}
        {tab === "mandate" && <MandateEditor />}
        {tab === "knowledge" && <KnowledgeLibrary />}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-3 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
        active ? "border-iv-green text-iv-fg" : "border-transparent text-iv-fg-3 hover:text-iv-fg"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
